/**
 * @file filter-coordinator.ts
 * @description Manages UI for image filters and adjustments.
 */

import { EventBus } from '@hcie/shared';
import { ImageOps } from '../services/image-ops';

export function initFilterCoordinator() {
    let originalSnapshot: ImageData | null = null;
    let activeType: string | null = null;

    (window as any).openFilterDialog = (type: string) => {
        const modal = document.getElementById('adjustmentModal');
        const title = document.getElementById('adjustmentTitle');
        const controls = document.getElementById('adjustmentControls');
        const applyBtn = document.getElementById('applyAdjustmentBtn');
        const previewCheck = document.getElementById('adjustmentPreview') as HTMLInputElement;

        if (!modal || !title || !controls || !applyBtn) return;

        controls.innerHTML = '';
        
        // Ensure floating position is centered on open (#2061-A)
        modal.style.display = 'flex'; 
        modal.style.left = '50%';
        modal.style.top = '150px';
        modal.style.transform = 'translateX(-50%)';
        
        activeType = type;

        // Take a snapshot of the current layer for previewing
        const globalLayers = (window as any).layers;
        const activeLayerIndex = (window as any).g?.activeLayerIndex ?? 0;
        const targetLayer = globalLayers ? globalLayers[activeLayerIndex] : null;
        
        if (targetLayer && targetLayer.ctx) {
            originalSnapshot = targetLayer.ctx.getImageData(0, 0, targetLayer.width, targetLayer.height);
        }

        // Helper: apply adjustment params to targetLayer via ImageOps and re-render
        const applyAdjustment = async (params: Parameters<typeof ImageOps.colorAdjust>[3]) => {
            if (!originalSnapshot || !targetLayer?.ctx) return;
            const w = targetLayer.width  || originalSnapshot.width;
            const h = targetLayer.height || originalSnapshot.height;
            const result = await ImageOps.colorAdjust(originalSnapshot.data, w, h, params);
            targetLayer.ctx.putImageData(new ImageData(result, w, h), 0, 0);
            EventBus.emit('RENDER_LAYERS');
        };

        const runPreview = () => {
            if (!previewCheck?.checked || !originalSnapshot || !targetLayer?.ctx) return;
            // Restore original first so sliders are always relative to the clean state
            targetLayer.ctx.putImageData(originalSnapshot, 0, 0);

            if (activeType === 'brightnesscontrast') {
                const brightness = parseInt((document.getElementById('filter_brightness') as HTMLInputElement).value);
                const contrast   = parseInt((document.getElementById('filter_contrast')   as HTMLInputElement).value);
                applyAdjustment({ brightness, contrast });
            } else if (activeType === 'huesaturation') {
                const hue        = parseInt((document.getElementById('filter_hue')        as HTMLInputElement).value);
                const saturation = parseInt((document.getElementById('filter_saturation') as HTMLInputElement).value);
                applyAdjustment({ hue, saturation });
            }
        };

        if (type === 'brightnesscontrast') {
            title.innerText = 'Brightness / Contrast';
            createSlider(controls, 'Brightness', 'filter_brightness', -100, 100, 0, runPreview);
            createSlider(controls, 'Contrast',   'filter_contrast',   -100, 100, 0, runPreview);

            applyBtn.onclick = async () => {
                if (targetLayer?.ctx && originalSnapshot) {
                    targetLayer.ctx.putImageData(originalSnapshot, 0, 0);
                }
                const brightness = parseInt((document.getElementById('filter_brightness') as HTMLInputElement).value);
                const contrast   = parseInt((document.getElementById('filter_contrast')   as HTMLInputElement).value);
                await applyAdjustment({ brightness, contrast });
                (window as any).closeAdjustmentModal?.();
            };
        } else if (type === 'huesaturation') {
            title.innerText = 'Hue / Saturation';
            createSlider(controls, 'Hue',        'filter_hue',        -180, 180, 0, runPreview);
            createSlider(controls, 'Saturation', 'filter_saturation', -100, 100, 0, runPreview);

            applyBtn.onclick = async () => {
                if (targetLayer?.ctx && originalSnapshot) {
                    targetLayer.ctx.putImageData(originalSnapshot, 0, 0);
                }
                const hue        = parseInt((document.getElementById('filter_hue')        as HTMLInputElement).value);
                const saturation = parseInt((document.getElementById('filter_saturation') as HTMLInputElement).value);
                await applyAdjustment({ hue, saturation });
                (window as any).closeAdjustmentModal?.();
            };
        }

        if (previewCheck) previewCheck.onchange = runPreview;
    };

    (window as any).closeAdjustmentModal = () => {
        const modal = document.getElementById('adjustmentModal');
        if (modal) modal.style.display = 'none';

        // Result rollback if cancelled
        if (originalSnapshot) {
            const globalLayers = (window as any).layers;
            const activeLayerIndex = (window as any).g?.activeLayerIndex ?? 0;
            const targetLayer = globalLayers ? globalLayers[activeLayerIndex] : null;
            if (targetLayer && targetLayer.ctx) {
                targetLayer.ctx.putImageData(originalSnapshot, 0, 0);
                EventBus.emit('RENDER_LAYERS');
            }
        }

        originalSnapshot = null;
        activeType = null;
    };
}

function createSlider(parent: HTMLElement, label: string, id: string, min: number, max: number, value: number, onUpdate: () => void) {
    const wrapper = document.createElement('div');
    wrapper.className = 'slider-wrapper';
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.gap = '5px';

    const labelRow = document.createElement('div');
    labelRow.style.display = 'flex';
    labelRow.style.justifyContent = 'space-between';
    
    const labelSpan = document.createElement('span');
    labelSpan.innerText = label;
    labelSpan.style.fontSize = '12px';
    labelSpan.style.color = '#ccc';
    
    const valueSpan = document.createElement('span');
    valueSpan.id = id + '_val';
    valueSpan.innerText = value.toString();
    valueSpan.style.fontSize = '12px';
    valueSpan.style.fontWeight = 'bold';
    valueSpan.style.color = 'var(--accent-primary, #0078d7)';

    labelRow.appendChild(labelSpan);
    labelRow.appendChild(valueSpan);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.id = id;
    slider.min = min.toString();
    slider.max = max.toString();
    slider.value = value.toString();
    slider.className = 'premium-slider'; // Assuming this has styles or we leverage generic ones
    slider.style.width = '100%';
    slider.style.cursor = 'pointer';
    slider.style.accentColor = 'var(--accent-primary, #0078d7)';
    
    slider.oninput = () => { 
        valueSpan.innerText = (slider.value > "0" ? "+" : "") + slider.value; 
        onUpdate();
    };

    wrapper.appendChild(labelRow);
    wrapper.appendChild(slider);
    parent.appendChild(wrapper);
}
